import { Box, Typography, useTheme } from "@mui/material";
import { InfoIcon } from "components/icons";
import { Tooltip } from "components/tooltip";
import type React from "react";
import { convertStringToSentenceCase } from "utilz/helpers";

interface LabelWithInfoProps {
  label?: React.ReactNode;
  required?: boolean;
  error?: boolean;
  /**
   * When provided, an info (i) icon is rendered after the label; hovering it
   * shows this content in a tooltip. Optional — omit it for a plain label.
   */
  tooltip?: React.ReactNode;
}

/**
 * Shared label renderer for form fields (Input, SelectField, …). Keeps the
 * exact label markup the fields already used and adds an optional, hoverable
 * info icon so any field can carry inline help without bespoke layout.
 */
export const LabelWithInfo = ({
  label,
  required = false,
  error = false,
  tooltip,
}: LabelWithInfoProps) => {
  const theme = useTheme();

  if (!label) return null;

  const labelText = (
    <Typography
      variant="body-medium"
      color={error ? "error" : "inherit"}
      textTransform="inherit"
    >
      {typeof label === "string" ? convertStringToSentenceCase(label) : label}
      {required && " *"}
    </Typography>
  );

  // No tooltip → render the bare label exactly as fields did before, so the
  // notched-outline legend measures the same width (no extra gap). The flex
  // wrapper is only introduced when there's actually an icon to align.
  if (!tooltip) return labelText;

  return (
    <Box
      component="span"
      sx={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
    >
      {labelText}

      <Tooltip title={tooltip} placement="top" disableSentenceCase>
        <Box
          component="span"
          aria-label="More information"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            cursor: "help",
            pointerEvents: "auto",
            color: theme.palette.lightBg.low,
          }}
          // Don't let the icon focus/open the field it labels.
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <InfoIcon sx={{ fontSize: 15 }} />
        </Box>
      </Tooltip>
    </Box>
  );
};
